import React, { useEffect, useRef } from 'react';
import { Col, Row, View } from '@amzn/stencil-react-components/layout';
import { useBreakpoints } from "@amzn/stencil-react-components/responsive";
import { Text } from '@amzn/stencil-react-components/text';
import { Card, LinkCard } from '@amzn/stencil-react-components/card';
import { getEnvironmentBasedRedirectionURL, getEnvironmentBasedURL, LoginMethod, isLanguageSelectorEnabled } from './Utils';
import { AtoZMyHRLogo, LeftImage, RightImage } from '../images/logos';
import './HomePage.css';
import { IconChevronRight, IconChevronLeft } from '@amzn/stencil-react-icons';
import LanguageSelectorDropdown from './LanguageSelectorDropdown';
import { useLocale } from '../context/LocaleContext';
import { useBundle } from '@amzn/react-arb-tools';
import { t } from '../helpers';
import { HOMEPAGE_MESSAGE_BUNDLE_NAME, HEARTBEAT_INTERVAL_SECONDS } from '../constants';
import { metricsHelper, MetricNames, DimensionNames, LoginMethodValues } from '../metrics';

export const HomePage = () => {
    const { matches } = useBreakpoints();
    const isMobile = matches.s || matches.m;
    const { locale, setLocale, resetLocale } = useLocale();
    const [bundle] = useBundle(HOMEPAGE_MESSAGE_BUNDLE_NAME);
    const isRTL = document.documentElement.dir === 'rtl';
    const ChevronIcon = isRTL ? IconChevronLeft : IconChevronRight;
    document.title = t(bundle, 'page_title') || "My HR Lite Welcome Page";

    // Read query parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const siteCode = urlParams?.get('site_code');
    const isMaask = urlParams?.get('maask') === 'true';
    const showLanguageSelector = isLanguageSelectorEnabled(siteCode);

    // Track if metrics have been published to avoid duplicate emissions (StrictMode runs effects twice)
    const metricsPublishedRef = useRef(false);
    const heartbeatStartedRef = useRef(false);

    // Publish page load metrics on mount
    useEffect(() => {
        if (metricsPublishedRef.current) {
            return;
        }
        metricsPublishedRef.current = true;

        // Publish PageLoad counter
        metricsHelper.publishCounter(MetricNames.PAGE_LOAD);

        // Publish PageLoadLatency timer using Performance API
        const latency = metricsHelper.getPageLoadLatency();
        if (latency !== null) {
            metricsHelper.publishTimer(MetricNames.PAGE_LOAD_LATENCY, latency);
        }
    }, []);

    // Heartbeat metric - emits every 15 minutes to indicate kiosk is alive
    useEffect(() => {
        if (heartbeatStartedRef.current) {
            return;
        }
        heartbeatStartedRef.current = true;

        const intervalMs = HEARTBEAT_INTERVAL_SECONDS * 1000;

        // Emit initial heartbeat (detailed with site_code + aggregate for alarms)
        metricsHelper.publishCounter(MetricNames.KIOSK_HEARTBEAT);
        metricsHelper.publishCounterAggregate(MetricNames.KIOSK_HEARTBEAT_AGGREGATE);

        // Set up interval for subsequent heartbeats
        const intervalId = setInterval(() => {
            metricsHelper.publishCounter(MetricNames.KIOSK_HEARTBEAT);
            metricsHelper.publishCounterAggregate(MetricNames.KIOSK_HEARTBEAT_AGGREGATE);
        }, intervalMs);

        // Cleanup on unmount
        return () => clearInterval(intervalId);
    }, []);

    // Handler for login button clicks
    const handleLoginClick = (loginMethod: LoginMethodValues) => {
        metricsHelper.publishCounter(MetricNames.LOGIN_BUTTON_CLICK, {
            [DimensionNames.LOGIN_METHOD]: loginMethod,
        });
    };
  return (
    <View >
        <Row className='header' justifyContent='space-between' alignItems='center'>
            <View margin="9px 0px 7px 32px">
                <AtoZMyHRLogo/>
            </View>
            {showLanguageSelector && (
                <LanguageSelectorDropdown
                    currentLocale={locale}
                    onChange={setLocale}
                    onReset={resetLocale}
                />
            )}
        </Row>
        <Row justifyContent='center' className="content-container">
            {!isMobile &&
                <Col width="100%" flex={1} justifyContent="flex-end" alignItems="flex-start">
                    <LeftImage/>
                </Col>
            }

            <Col width="100%" flex={2} alignItems='center' className="center-content">
                <Row alignItems='center' margin="48px 0 16px 0">
                    <Text textAlign='center' fontSize="40px" fontWeight="bold" lineHeight="52px">
                        {t(bundle, 'welcome_title') || 'Welcome to the A to Z | My HR Kiosk'}
                    </Text>
                </Row>

                {!isMaask && (
                    <Row alignItems='center' margin="0 0 48px 0">
                        <Text textAlign='center' fontSize="20px" fontWeight="bold" lineHeight="32px">
                            {t(bundle, 'login_to_begin') || 'Log in to A to Z to begin'}
                        </Text>
                    </Row>
                )}

                <Row alignItems='center' >
                    <Card minWidth={'580px'} minHeight={ isMaask ? "320px" : "204px"} padding={'32px'}>
                        <Col gridGap={24} width="100%">
                            {isMaask && (
                                <Text textAlign='center' fontSize="20px" fontWeight="bold" lineHeight="32px" >
                                    {t(bundle, 'choose_login_method') || 'Choose a log in method to begin'}
                                </Text>
                            )}
                            { isMaask &&
                                <LinkCard
                                    isElevated={true}
                                    href={getEnvironmentBasedRedirectionURL(LoginMethod.MAASK)}
                                    onClick={() => handleLoginClick(LoginMethodValues.MAASK)}
                                >
                                    <Col gridGap={4} flex={4}>
                                        <Text fontSize="20px" fontWeight='bold' lineHeight="32px">
                                            {t(bundle, 'login_with_badge') || 'Log in with your Badge'}
                                        </Text>
                                        <Text fontSize="16px" fontWeight='regular' lineHeight="24px">
                                            {t(bundle, 'login_with_badge_description') || 'Scan your badge and enter pin.'}
                                        </Text>
                                    </Col>
                                    <Col flex={1} alignItems='center' justifyContent='center'>
                                        <ChevronIcon size={32} aria-hidden={true}/>
                                    </Col>
                                </LinkCard>
                            }
                            <LinkCard
                                isElevated={true}
                                href={getEnvironmentBasedRedirectionURL(LoginMethod.ATOZ)}
                                onClick={() => handleLoginClick(LoginMethodValues.ATOZ)}
                            >
                                <Col gridGap={4} flex={4}>
                                    <Text fontSize="20px" fontWeight='bold' lineHeight="32px">
                                        {isMaask
                                            ? (t(bundle, 'login_with_atoz') || 'Log in with A to Z')
                                            : (t(bundle, 'atoz_login') || 'A to Z log in')}
                                    </Text>
                                    <Text fontSize="16px" fontWeight='regular' lineHeight="24px">
                                        {t(bundle, 'login_with_atoz_description') || 'Enter A to Z username and password.'}
                                    </Text>
                                </Col>
                                <Col flex={1} alignItems='center' justifyContent='center'>
                                    <ChevronIcon size={32} aria-hidden={true}/>
                                </Col>
                            </LinkCard>
                        </Col>
                    </Card>
                </Row>
                <Row alignItems='center' justifyContent='center' margin="100px 0 0 0">
                    <Row alignItems='center' width="580px" gridGap={'24px'}>
                        <Text fontSize={'20px'} lineHeight="28px" style={{ flex: 1 }}>
                            {isMaask
                                ? (t(bundle, 'qr_code_prompt_maask') || "Don't remember your MAASK pin? Scan the QR code to access the A to Z | My HR Kiosk on your phone.")
                                : (t(bundle, 'qr_code_prompt_password') || "Don't remember your password? Scan the QR code to access the A to Z | My HR Kiosk on your phone.")}
                        </Text>
                        <img
                            role="presentation"
                            height="92px"
                            width="92px"
                            alt={t(bundle, 'qr_code_alt') || "QR code"}
                            src={getEnvironmentBasedURL('QRCode')}
                            onError={(e) => {
                                e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI5MiIgaGVpZ2h0PSI5MiIgdmlld0JveD0iMCAwIDkyIDkyIj48cmVjdCBmaWxsPSIjZjBmMGYwIiB3aWR0aD0iOTIiIGhlaWdodD0iOTIiIHJ4PSI4Ii8+PHRleHQgeD0iNDYiIHk9IjQyIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmb250LWZhbWlseT0ic2Fucy1zZXJpZiIgZm9udC1zaXplPSIxMCIgZmlsbD0iIzk5OSI+UVI8L3RleHQ+PHRleHQgeD0iNDYiIHk9IjU2IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmb250LWZhbWlseT0ic2Fucy1zZXJpZiIgZm9udC1zaXplPSIxMCIgZmlsbD0iIzk5OSI+Q29kZTwvdGV4dD48L3N2Zz4=';
                            }}
                        />
                    </Row>
                </Row>
            </Col>

            {!isMobile &&
                <Col width="100%" flex={1} justifyContent="flex-end" alignItems="flex-end">
                    <RightImage/>
                </Col>
            }
        </Row>
    </View>
  );
};
